import type { StorefrontProduct } from './dropify'

export type OrderUnit = 'box' | 'lusin'

export type ProductOrderFacts = {
  minimumLabel: string
  orderUnit: OrderUnit
  packLabel?: string
  sizeGuidance?: string
}

export const CATEGORY_ORDER_RULES = {
  'bra-lingerie': { minimum: 25, unit: 'pcs' },
  'cd-underwear': { minimum: 50, unit: 'pcs' },
  'kaos-kaki': { minimum: 50, unit: 'pasang' },
  'manshet-sarung-tangan': { minimum: 25, unit: 'pasang' },
  handuk: { minimum: 10, unit: 'pcs' },
} as const

export const GEKKO_MINIMUM_LUSIN = 50
export const MANSHET_MINIMUM_LUSIN = 50
export const MEDICAL_GLOVE_MINIMUM_BOX = 10

const PRIORITY_META_DESCRIPTIONS: Record<string, string> = {
  'COMET-CG813':
    'Sarung Tangan COMET CG 813 ESD dengan rajut carbon dan PU di ujung jari. Untuk semikonduktor dan perakitan elektronik. Cek stok serta quotation via WhatsApp.',
  'TALLY-BH-3070':
    'Grosir Daily Bra Tally BH 3070 untuk kebutuhan toko. Periksa stok, satuan pembelian, dan quotation melalui WhatsApp.',
  'BRA-OREO-223':
    'Grosir Bra Oreo 223 untuk kebutuhan toko. Periksa stok, satuan pembelian, dan quotation melalui WhatsApp.',
  'CD-WANITA-1501-PINNIFEN':
    'Grosir CD Wanita 1501 Pinnifen untuk kebutuhan toko. Periksa stok, satuan pembelian, dan quotation melalui WhatsApp.',
  'MANSHET-GEKKO-HITAM':
    'Sarung Tangan Gekko Grip Nitrile Hitam berlapis nitrile untuk bengkel, otomotif, mekanikal, dan pekerjaan kotor. Cek stok serta quotation via WhatsApp.',
  'MANSHET-GEKKO-BIRU':
    'Sarung Tangan Gekko Grip Nitrile Biru berlapis nitrile untuk perawatan mesin, komponen, bengkel, dan area kerja berminyak. Cek stok serta quotation via WhatsApp.',
}

function hasCategory(product: StorefrontProduct, slug: string) {
  return product.categories.some((category) => category.slug === slug)
}

export function isMaskProduct(product: StorefrontProduct) {
  return hasCategory(product, 'perlengkapan-medis') && /\bmasker\b/i.test(product.name)
}

export function isMedicalGloveProduct(product: StorefrontProduct) {
  return hasCategory(product, 'perlengkapan-medis') && !isMaskProduct(product)
}

export function isNonMedicalMaskProduct(product: StorefrontProduct) {
  return !hasCategory(product, 'perlengkapan-medis') && /\bmasker\b/i.test(`${product.name} ${product.slug}`)
}

export function isGekkoProduct(product: StorefrontProduct) {
  return product.tags.some((tag) => tag.slug === 'gekko-grip') || /\bgekko(?:\s+grip)?\b/i.test(product.name)
}

export function getProductOrderFacts(product: StorefrontProduct): ProductOrderFacts | null {
  // This bra is currently assigned to CD upstream. Do not present the CD MOQ
  // as a verified buying requirement while the product record is being reviewed.
  if (product.sku === 'TALLY-BH-3070' || product.sku === 'BRA-OREO-223') {
    return {
      minimumLabel: 'Minimum order dan isi kemasan model ini dikonfirmasi admin sebelum quotation.',
      orderUnit: 'lusin',
    }
  }
  if (isMaskProduct(product)) {
    const packMatch = product.name.match(/box\s+(\d+)\s+pcs/i)
    return {
      minimumLabel: 'Minimum order masker mengikuti tipe dan dikonfirmasi admin sebelum quotation.',
      orderUnit: 'box',
      packLabel: packMatch ? `Satu box berisi ${packMatch[1]} pcs.` : undefined,
    }
  }

  if (isMedicalGloveProduct(product)) {
    return {
      minimumLabel: `Minimum order mulai ${MEDICAL_GLOVE_MINIMUM_BOX} box.`,
      orderUnit: 'box',
      sizeGuidance: 'Ukuran sarung tangan tersedia mulai XS sampai XL. Admin akan mengonfirmasi ukuran yang tersedia untuk produk yang Anda pilih.',
    }
  }

  if (isNonMedicalMaskProduct(product)) {
    return {
      minimumLabel: 'Minimum order produk ini dikonfirmasi admin sebelum quotation.',
      orderUnit: 'lusin',
      packLabel: 'Produk dijual per lusin mengikuti kemasan yang tersedia.',
    }
  }

  if (isGekkoProduct(product)) {
    return {
      minimumLabel: `Minimum order Gekko Grip mulai ${GEKKO_MINIMUM_LUSIN} lusin atau ${GEKKO_MINIMUM_LUSIN * 12} pasang.`,
      orderUnit: 'lusin',
      packLabel: 'Satu lusin berisi 12 pasang.',
    }
  }

  if (hasCategory(product, 'manshet-sarung-tangan') && /\bmans?het\b/i.test(product.name)) {
    return {
      minimumLabel: `Minimum order manset mulai ${MANSHET_MINIMUM_LUSIN} lusin.`,
      orderUnit: 'lusin',
      packLabel: 'Satu lusin berisi 12 pcs.',
    }
  }

  const categorySlug = product.categories[0]?.slug as keyof typeof CATEGORY_ORDER_RULES | undefined
  const rule = categorySlug ? CATEGORY_ORDER_RULES[categorySlug] : undefined
  if (!rule) return null

  return {
    minimumLabel: `Minimum order kategori ini mulai ${rule.minimum} lusin.`,
    orderUnit: 'lusin',
    packLabel: `Satu lusin berisi 12 ${rule.unit}.`,
  }
}

export function maskMetaDescription(product: StorefrontProduct) {
  const facts = getProductOrderFacts(product)
  const pack = facts?.packLabel ? ` ${facts.packLabel}` : ''
  return `Grosir ${product.name} per box dari Dropify Surabaya.${pack} Minimum pemesanan mengikuti tipe. Cek stok dan quotation via WhatsApp.`
}

export function normalizeProductOrderingFacts(product: StorefrontProduct): StorefrontProduct {
  if (isMaskProduct(product)) return { ...product, meta_description: maskMetaDescription(product) }

  const priorityDescription = PRIORITY_META_DESCRIPTIONS[product.sku]
  if (priorityDescription) {
    return {
      ...product,
      meta_description: priorityDescription,
    }
  }

  return product
}
